import React, { useEffect, useState } from "react";
import { loadFonts } from "./CardGenerator";
import "./CardEditor.css";
import { CardData, DeckInfo, FontStyleOverride, UiFontStyleOverride } from "./types";
import CgoCardSide from "./CgoCard";
import { useParams } from "react-router-dom";
import { ProjectFontDefinition } from "src/components/types";
import { Button } from "@mui/material";
import FontEditor from "src/components/pages/projectAdmin/FontEditor";
import { cloneDeep } from "lodash";

type OverrideType = "deck" | "card";

function CardEditor(props: {
  deckData: DeckInfo;
  cards: CardData;
  globalAssets: { [key: string]: any };
  deckName: string;
  projectFonts: Array<ProjectFontDefinition>;
  deckFontOverrides: Array<FontStyleOverride>;
  cardFontOverrides: Array<FontStyleOverride>;
}) {
  const [selectedCard, setSelectedCard] = useState(Object.keys(props.deckData.cards ?? {})[0]);
  const [isRev, setIsRev] = useState(false);
  const [isDebug, setIsDebug] = useState(false);
  const [compactMode, setCompactMode] = useState(true);
  const [selectedTab, setSelectedTab] = useState("parameters");

  let deckFontOverridesFromProps = cloneDeep(props.deckFontOverrides) as UiFontStyleOverride[];
  deckFontOverridesFromProps.length > 0 && (deckFontOverridesFromProps[0].selected = true);

  const [deckFontOverrides, setDeckFontOverrides] = useState(deckFontOverridesFromProps || []);
  const [cardFontOverrides, setCardFontOverrides] = useState((props.cardFontOverrides as UiFontStyleOverride[]) || []);
  const [deckFontSaveStatus, setDeckFontSaveStatus] = useState("");
  const [cardFontSaveStatus, setCardFontSaveStatus] = useState("");
  const [deckFontProcessing, setDeckFontProcessing] = useState(false);
  const [cardFontProcessing, setCardFontProcessing] = useState(false);

  const [fontOverrideForCopy, setFontOverrideForCopy] = useState<FontStyleOverride | null>(null);

  const [showPreviewCards, setShowPreviewCards] = useState(false);

  const activeCards = (showPreviewCards ? props.deckData.previewCards : null) ?? props.deckData.cards;
  const activeDeckInfo =
    showPreviewCards && props.deckData.previewCards
      ? { ...props.deckData, cards: props.deckData.previewCards }
      : props.deckData;

  const savePosition = localStorage.getItem("card-viewer-save-position") === "1";
  let params = useParams();
  let projectSlug = params.projectSlug;

  useEffect(() => {
    loadFonts(window, props.deckData);
  }, [props.deckData]);

  useEffect(() => {
    if (savePosition) {
      let savedCardId = localStorage.getItem("card-viewer-selected-card-id-" + projectSlug + "-" + props.deckName);
      if (savedCardId) {
        setSelectedCard(savedCardId);
      }
    }
  }, [projectSlug, props.deckName, savePosition]);

  useEffect(() => {
    const newCards = (showPreviewCards ? props.deckData.previewCards : null) ?? props.deckData.cards;
    const newCardIds = Object.keys(newCards ?? {});
    setSelectedCard((prev) => (newCardIds.includes(prev) ? prev : newCardIds[0]));
  }, [showPreviewCards, props.deckData]);

  useEffect(() => {
    if (selectedCard) {
      setCardFontOverrides((prev) => {
        let found = false;
        return prev.map((override) => {
          if (!found && override.cardName === selectedCard) {
            found = true;
            return { ...override, selected: true };
          }
          return { ...override, selected: false };
        });
      });
    }
    if (savePosition && selectedCard) {
      localStorage.setItem("card-viewer-selected-card-id-" + projectSlug + "-" + props.deckName, selectedCard);
    }
  }, [projectSlug, props.deckName, savePosition, selectedCard]);

  let deckData = props.deckData;
  let cardIds = Object.keys(activeCards ?? {});

  function stepCardList(diff: number) {
    let currIndex = cardIds.indexOf(selectedCard);
    let newIndex = currIndex + diff;
    let newId = cardIds[newIndex];
    if (!newId) {
      return;
    }
    setSelectedCard(newId);
  }

  function getCardName(cardId: string) {
    let rules = props.deckData.rules;
    let cardParameters = activeCards[cardId];
    if (!cardParameters || !rules?.find || !rules.find((r) => r.rule === "editorCardName")) {
      return "";
    }
    let nameField = rules.find((r) => r.rule === "editorCardName")?.arg;
    if (!nameField || !(nameField in cardParameters)) {
      return "";
    }
    return cardParameters[nameField];
  }

  if (!deckData) {
    return <></>;
  }

  if (!activeCards) {
    return <div>No cards info</div>;
  }

  let cardParameters = activeCards[selectedCard];
  if (!cardParameters) {
    return <div>Cannot find card parameters (did you forget to define "ID"?)</div>;
  }

  let cardSelect = (
    <select onChange={(evt) => setSelectedCard(evt.target.value)} value={selectedCard}>
      {cardIds.map((cardId) => {
        let cardName = getCardName(cardId);
        return (
          <option key={cardId} value={cardId}>
            {cardId}
            {cardName !== "" ? ` (${cardName})` : ""}
          </option>
        );
      })}
    </select>
  );

  let cardPreviewStyle = {};
  let cardWidth = props.deckData.cardInfo?.w ?? 100;
  let cardHeight = props.deckData.cardInfo?.h ?? 200;
  let viewportHeight = window.innerHeight;
  let previewScale = Math.min(1, viewportHeight / 2 / cardHeight);
  let previewWidth = cardWidth * previewScale;
  let previewHeight = cardHeight * previewScale;

  if (compactMode) {
    cardPreviewStyle = {
      width: previewWidth,
      height: previewHeight,
      transform: `scale(${previewScale})`,
      transformOrigin: "top left",
    };
  }

  let cardPreview = (
    <div className={"card-preview-wrap" + (isDebug ? " debug" : "")}>
      <div className="form-card-preview" style={cardPreviewStyle}>
        <CgoCardSide
          cardId={selectedCard}
          deckInfo={activeDeckInfo}
          globalAssets={props.globalAssets}
          isRev={isRev}
          isDebug={isDebug}
          projectFonts={props.projectFonts}
          deckFontOverrides={deckFontOverrides}
          cardFontOverrides={cardFontOverrides.filter((override) => override.cardName === selectedCard)}
          useHighResImages={true}
        />
      </div>
    </div>
  );

  let revSelect = (
    <div>
      <label>Rev:</label>
      <input type="checkbox" checked={isRev} onChange={(evt) => setIsRev(evt.target.checked)} />
    </div>
  );
  if (!deckData.rulesRev) {
    revSelect = <></>;
  }

  let parametersTable = (
    <table border={1} className="preview-parameters">
      <tbody>
        {Object.keys(cardParameters).map((parameterName) => (
          <tr key={parameterName}>
            <th>{parameterName}</th>
            <td>{cardParameters[parameterName]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  function deleteFontOverride(id: string, overrideType: OverrideType) {
    if (overrideType === "deck") {
      setDeckFontProcessing(true);
    } else {
      setCardFontProcessing(true);
    }
    fetch(`/api/projects/${projectSlug}/decks/${props.deckName}/delete-font`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ id }),
    })
      .then((response) => {
        if (response.ok) {
          if (overrideType === "deck") {
            setDeckFontProcessing(false);
            setDeckFontOverrides((prev) => {
              let filtered = prev.filter((override) => override.id !== id);
              filtered[0].selected = true;
              return filtered;
            });
          } else {
            setCardFontProcessing(false);
            setCardFontOverrides((prev) => {
              let filtered = prev.filter((override) => override.id !== id);
              filtered[0].selected = true;
              return filtered;
            });
          }
        }
      })
      .catch((error) => {
        console.error("Error deleting font override:", error);
      });
  }

  function addFontOverride(overrideType: OverrideType) {
    const newOverride: UiFontStyleOverride = {
      id: crypto.randomUUID(),
      deckName: props.deckName,
      layerName: "",
      projectFont: "",
      fontFamily: "",
      fontWeight: "",
      fontStyle: "",
      fontSize: "",
      fontColor: "",
      lineHeight: "",
      iconHeight: "",
      strokeWidth: "",
      strokeColor: "",
      subtextId: "",
      margin: "",
      iconMargin: "",
      textAlign: "",
      conditionParameter: "",
      conditionValue: "",
      conditionOrder: 0,
      selected: true,
      unsaved: true,
    };
    if (overrideType === "deck") {
      setDeckFontOverrides((prev) => [newOverride, ...prev.map((override) => ({ ...override, selected: false }))]);
    } else {
      newOverride.cardName = selectedCard;
      setCardFontOverrides((prev) => [newOverride, ...prev.map((override) => ({ ...override, selected: false }))]);
    }
  }

  function updateFontOverride(updatedOverride: UiFontStyleOverride, overrideType: OverrideType) {
    updatedOverride.unsaved = true;
    if (overrideType === "deck") {
      setDeckFontOverrides((prev) =>
        prev.map((override) => (override.id === updatedOverride.id ? updatedOverride : override)),
      );
    } else {
      setCardFontOverrides((prev) =>
        prev.map((override) => (override.id === updatedOverride.id ? updatedOverride : override)),
      );
    }
  }

  function copyFontOverride(id: string, overrideType: OverrideType) {
    let overrideToCopy: FontStyleOverride | undefined;
    if (overrideType === "deck") {
      overrideToCopy = deckFontOverrides.find((override) => override.id === id);
    } else {
      overrideToCopy = cardFontOverrides.find((override) => override.id === id);
    }
    if (!overrideToCopy) {
      return;
    }
    const newOverride: UiFontStyleOverride = {
      ...overrideToCopy,
      id: crypto.randomUUID(),
    };
    setFontOverrideForCopy(newOverride);
  }

  function addFontOverrideFromCopy(overrideType: OverrideType) {
    if (!fontOverrideForCopy) {
      return;
    }
    const newOverride = { ...fontOverrideForCopy, id: crypto.randomUUID(), selected: true, unsaved: true };
    if (overrideType === "deck") {
      setDeckFontOverrides((prev) => [newOverride, ...prev.map((override) => ({ ...override, selected: false }))]);
    } else {
      newOverride.cardName = selectedCard;
      setCardFontOverrides((prev) => [newOverride, ...prev.map((override) => ({ ...override, selected: false }))]);
    }
  }

  function saveDeckFonts() {
    setDeckFontProcessing(true);
    fetch(`/api/projects/${projectSlug}/decks/${props.deckName}/fonts`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(deckFontOverrides),
    })
      .then((response) => {
        if (response.ok) {
          setDeckFontOverrides((prev) => prev.map((override) => ({ ...override, unsaved: false })));
          setDeckFontProcessing(false);
          setDeckFontSaveStatus("Deck fonts saved successfully.");
          setTimeout(() => setDeckFontSaveStatus(""), 3000);
        }
      })
      .catch((error) => {
        console.error("Error saving deck fonts:", error);
        setDeckFontProcessing(false);
        setDeckFontSaveStatus("Error saving deck fonts.");
        setTimeout(() => setDeckFontSaveStatus(""), 3000);
      });
  }

  function saveCardFonts() {
    setCardFontProcessing(true);
    fetch(`/api/projects/${projectSlug}/decks/${props.deckName}/fonts`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(cardFontOverrides),
    })
      .then((response) => {
        if (response.ok) {
          setCardFontOverrides((prev) => prev.map((override) => ({ ...override, unsaved: false })));
          setCardFontProcessing(false);
          setCardFontSaveStatus("Card fonts saved successfully.");
          setTimeout(() => setCardFontSaveStatus(""), 3000);
        }
      })
      .catch((error) => {
        console.error("Error saving card fonts:", error);
        setCardFontProcessing(false);
        setCardFontSaveStatus("Error saving card fonts.");
        setTimeout(() => setCardFontSaveStatus(""), 3000);
      });
  }

  function fontEditorListItemClick(
    event: React.MouseEvent<HTMLDivElement>,
    overrideId: string,
    overrideType: OverrideType,
  ) {
    event.stopPropagation();
    if (event.ctrlKey || event.metaKey) {
      if (overrideType === "deck") {
        setDeckFontOverrides((prev) =>
          prev.map((override) =>
            override.id === overrideId ? { ...override, selected: !override.selected } : override,
          ),
        );
      } else {
        setCardFontOverrides((prev) =>
          prev.map((override) =>
            override.id === overrideId ? { ...override, selected: !override.selected } : override,
          ),
        );
      }
    } else {
      if (overrideType === "deck") {
        setDeckFontOverrides((prev) => prev.map((override) => ({ ...override, selected: override.id === overrideId })));
      } else {
        setCardFontOverrides((prev) => prev.map((override) => ({ ...override, selected: override.id === overrideId })));
      }
    }
  }

  const deckFontEditor = (
    <FontEditor
      saveFonts={saveDeckFonts}
      fontProcessing={deckFontProcessing}
      fontSaveStatus={deckFontSaveStatus}
      addFontOverride={() => addFontOverride("deck")}
      addFontOverrideFromCopy={() => addFontOverrideFromCopy("deck")}
      fontOverrideForCopy={fontOverrideForCopy}
      fontOverrides={deckFontOverrides}
      fontEditorListItemClick={(event, id) => fontEditorListItemClick(event, id, "deck")}
      removeFontOverride={(id: string) => deleteFontOverride(id, "deck")}
      updateFontOverride={(updatedOverride: UiFontStyleOverride) => updateFontOverride(updatedOverride, "deck")}
      copyFontOverride={(id: string) => copyFontOverride(id, "deck")}
      projectFonts={props.projectFonts}
      layerNames={props.deckData.layers?.map((layer) => layer.name)}
      cardParameters={activeCards ? Object.keys(activeCards[selectedCard] ?? {}) : []}
    />
  );

  const cardFontEditor = (
    <FontEditor
      saveFonts={saveCardFonts}
      fontProcessing={cardFontProcessing}
      fontSaveStatus={cardFontSaveStatus}
      addFontOverride={() => addFontOverride("card")}
      addFontOverrideFromCopy={() => addFontOverrideFromCopy("card")}
      fontOverrideForCopy={fontOverrideForCopy}
      fontOverrides={cardFontOverrides.filter((override) => override.cardName === selectedCard)}
      fontEditorListItemClick={(event, id) => fontEditorListItemClick(event, id, "card")}
      removeFontOverride={(id: string) => deleteFontOverride(id, "card")}
      updateFontOverride={(updatedOverride: UiFontStyleOverride) => updateFontOverride(updatedOverride, "card")}
      copyFontOverride={(id: string) => copyFontOverride(id, "card")}
      projectFonts={props.projectFonts}
      layerNames={props.deckData.layers?.map((layer) => layer.name)}
      cardParameters={activeCards ? Object.keys(activeCards[selectedCard] ?? {}) : []}
    />
  );

  return (
    <div className="card-editor-main">
      <div className="card-editor-left">
        <div className="card-preview-options">
          {revSelect}
          <div>
            <label>Debug mode:</label>
            <input type="checkbox" checked={isDebug} onChange={(evt) => setIsDebug(evt.target.checked)} />
          </div>
          <div>
            <label>Compact mode:</label>
            <input type="checkbox" checked={compactMode} onChange={(evt) => setCompactMode(evt.target.checked)} />
          </div>
          <div>
            <label>Show preview cards:</label>
            <input
              type="checkbox"
              checked={showPreviewCards}
              onChange={(evt) => setShowPreviewCards(evt.target.checked)}
            />
          </div>
        </div>
        <div className="card-preview-controls">
          <button onClick={() => stepCardList(-1)}>⬅️</button>
          {cardSelect}
          <button onClick={() => stepCardList(1)}>➡️</button>
        </div>
        <div className={"card-preview-params-container" + (compactMode ? " compact" : "")}>{cardPreview}</div>
      </div>
      <div className="card-editor-right">
        <div className="card-editor-right-controls">
          <Button
            variant={selectedTab === "parameters" ? "contained" : "outlined"}
            onClick={() => setSelectedTab("parameters")}
          >
            Parameters
          </Button>
          <Button
            variant={selectedTab === "deckFonts" ? "contained" : "outlined"}
            onClick={() => setSelectedTab("deckFonts")}
          >
            {"Deck fonts" + (deckFontOverrides.length > 0 ? ` (${deckFontOverrides.length})` : "")}
          </Button>
          <Button
            variant={selectedTab === "cardFonts" ? "contained" : "outlined"}
            onClick={() => setSelectedTab("cardFonts")}
          >
            {"Card fonts" +
              (cardFontOverrides.length > 0
                ? ` (${cardFontOverrides.filter((fs) => fs.cardName === selectedCard).length})`
                : "")}
          </Button>
        </div>
        {selectedTab === "parameters" && parametersTable}
        {selectedTab === "deckFonts" && deckFontEditor}
        {selectedTab === "cardFonts" && cardFontEditor}
      </div>
    </div>
  );
}

export default CardEditor;
